import { z } from "zod";

// Reject `javascript:`, `data:`, `vbscript:`, etc. — `url` and `photoUrl` flow
// into <a href> / CSS url(...). Both XSS surfaces.
const httpUrl = z.string().regex(/^https?:\/\//i);
// A connected club's own booking page. https only, same rule as the CourtsApp
// app and the MCP's ChatGPT widget.
const httpsUrl = z.string().regex(/^https:\/\//i);
// Average review score, 0-5. Not sent by the MCP yet; the card shows a rating
// chip once it is.
const rating = z.number().min(0).max(5).nullable().optional();

export const AvailabilitySlotSchema = z.object({
  id: z.string().min(1),
  surface: z.string(),
  // Facility-local. Pinned so a format drift drops the card (logged) instead of
  // rendering a blank day label or "9:00 AM AM".
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  duration: z.number(),             // minutes
  originalPrice: z.number(),        // per-hour cents
  discountedPrice: z.number(),      // per-hour cents
  url: httpUrl,
});

export const AvailabilityFacilitySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sport: z.string().min(1),
  sportSlug: z.string().min(1),
  // Server can return null for facilities without a hero photo.
  photoUrl: httpUrl.nullable().optional(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string().optional(),
  distance: z.number(),             // miles
  url: httpUrl,
  rating,
  // Bookable clubs omit listingTier today; accept it if the server sends it.
  listingTier: z.literal("bookable").optional(),
  slots: z.array(AvailabilitySlotSchema).min(1),
});

/**
 * A connected club (courtsapp/courtsapp docs/listing-tiers.md): it books on
 * its own site, so it has no slots and no CourtsApp checkout. The MCP appends
 * up to two after the bookable clubs. `distance` is 0 when the server did not
 * compute it. `paidListing` / `sports` / `courtCount` match the app's
 * connected card and are optional until the MCP sends them.
 */
export const ConnectedFacilitySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sport: z.string().optional(),
  photoUrl: httpUrl.nullable().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  distance: z.number().optional(),
  url: httpUrl,                     // the club's CourtsApp page ("More info")
  listingTier: z.literal("connected"),
  bookingUrl: httpsUrl,
  clickTrackUrl: httpUrl.optional(),
  paidListing: z.boolean().optional(),
  sports: z.array(z.string()).optional(),
  courtCount: z.number().nullable().optional(),
  rating,
});

/**
 * A listed club (docs/listing-tiers.md): a directory entry with no booking
 * flow at all, so the card is just name, place and a "More info" link to its
 * CourtsApp page. The MCP does not send listed clubs today (search-availability
 * only appends connected ones); this keeps the widget ready for when it does.
 */
export const ListedFacilitySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sport: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  distance: z.number().optional(),
  url: httpUrl,
  listingTier: z.literal("listed"),
});

const ResultEnvelopeSchema = z.object({
  sportId: z.string().min(1),
  facilities: z.array(z.unknown()),
  hint: z.string().optional(),
});

export type AvailabilitySlot = z.infer<typeof AvailabilitySlotSchema>;
export type BookableFacility = z.infer<typeof AvailabilityFacilitySchema>;
export type ConnectedFacility = z.infer<typeof ConnectedFacilitySchema>;
export type ListedFacility = z.infer<typeof ListedFacilitySchema>;
export type AvailabilityFacility = BookableFacility | ConnectedFacility | ListedFacility;
export type AvailabilityResult = {
  sportId: string;
  facilities: AvailabilityFacility[];
  hint?: string;
};

export function isConnectedFacility(
  facility: AvailabilityFacility,
): facility is ConnectedFacility {
  return facility.listingTier === "connected";
}

export function isListedFacility(
  facility: AvailabilityFacility,
): facility is ListedFacility {
  return facility.listingTier === "listed";
}

function parseFacility(value: unknown): AvailabilityFacility | null {
  const tier = (value as { listingTier?: unknown } | null)?.listingTier;
  const schema =
    tier === "connected"
      ? ConnectedFacilitySchema
      : tier === "listed"
        ? ListedFacilitySchema
        : AvailabilityFacilitySchema;
  const parsed = schema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Validates each facility on its own and drops the bad ones, so one club the
 * widget can't draw (a new tier, a bad URL) no longer hides the whole row.
 */
export function validateAvailabilityResult(value: unknown): AvailabilityResult | null {
  const envelope = ResultEnvelopeSchema.safeParse(value);
  if (!envelope.success) return null;
  const facilities: AvailabilityFacility[] = [];
  for (const raw of envelope.data.facilities) {
    const facility = parseFacility(raw);
    if (facility) {
      facilities.push(facility);
    } else {
      console.warn("[availability] dropped facility that failed validation", {
        id: (raw as { id?: unknown } | null)?.id,
      });
    }
  }
  return { sportId: envelope.data.sportId, facilities, hint: envelope.data.hint };
}
